import React, { useState, useEffect, useCallback } from 'react';
import {
  RACTextField,
  makeStyles,
  InputAdornment,
} from '@rentacenter/racstrap';
import debounce from 'lodash/debounce';

// Simple search icon component to avoid Material-UI dependency
const SearchIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8"></circle>
    <path d="m21 21-4.35-4.35"></path>
  </svg>
);

const useStyles = makeStyles((theme) => ({
  searchContainer: {
    // padding: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
  },
  searchField: {
    '& .MuiInputBase-root': {
      fontSize: '0.8rem',
      borderRadius: 8,
      minHeight: '36px',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 8,
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 12px',
    },
    '& .MuiFormLabel-root': {
      fontSize: '0.8rem',
    },
  },
}));

interface SearchNotesProps {
  onSearch?: (query: string) => void;
  isSearching?: boolean;
}

/**
 * Component for searching notes
 */
export const SearchNotes: React.FC<SearchNotesProps> = ({
  onSearch,
  isSearching,
}) => {
  const classes = useStyles();
  const [query, setQuery] = useState('');

  // Create debounced search function to avoid too many API calls
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearch = useCallback(
    debounce((q: string) => {
      if (onSearch) {
        onSearch(q);
      }
    }, 500),
    [onSearch]
  );

  // Effect to trigger search when query changes
  useEffect(() => {
    if (onSearch) {
      debouncedSearch(query);
    }
    // Cancel debounced call on cleanup
    return () => {
      debouncedSearch.cancel();
    };
  }, [query, debouncedSearch, onSearch]);

  // Handle search input change
  const handleQueryChange = (value: string) => {
    setQuery(value);
  };

  return (
    <div className={classes.searchContainer}>
      <RACTextField
        className={classes.searchField}
        // label='Search notes'
        variant="outlined"
        fullWidth
        value={query}
        onChange={handleQueryChange}
        disabled={isSearching}
        placeholder="Search notes..."
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
    </div>
  );
};
